import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, BackTop } from 'antd';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Header from '../Header/Header';
import Sider from '../Sider/Sider';
import Footer from '../Footer/Footer';
import './Layout.scss';
import TwoFactorBanner from '../Banner/TwoFactorBanner';
import { SectionGuidanceVisibilityProvider } from '../../contexts/SectionGuidanceVisibilityContext';
import ContextSwitchOverlay from '../ContextSwitchOverlay/ContextSwitchOverlay';
import { switchingContextSelector, contextSwitchMessageSelector } from '../App/AppSelectors';
import { showNotificationSelector, showSmsSettingBannerSelector } from '../../scenes/Admin/scenes/User/UserSelectors';

const { Content } = Layout;
const WIDTH_SIDER = 280;
const MOBILE_MQ = '(max-width: 1024px)';

function PageTransition({ children }) {
  const [isSettled, setIsSettled] = useState(false);

  return (
    <div
      className={classNames('page-transition', { 'page-transition-done': isSettled })}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) setIsSettled(true);
      }}
    >
      {children}
    </div>
  );
}

export default ({ children, sider, hasSider, bg }) => {
  const [siderVisible, setSiderVisible] = useState(false);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
  );
  const hasNavigationSider = Boolean(sider || hasSider);
  const location = useLocation();

  const showNotification = useSelector(showNotificationSelector);
  const showSmsSettingBanner = useSelector(showSmsSettingBannerSelector);
  const switchingContext = useSelector(switchingContextSelector);
  const contextSwitchMessage = useSelector(contextSwitchMessageSelector);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const handler = (e) => {
      setIsMobile(e.matches);
      setSiderCollapsed(e.matches);
      if (!e.matches) setSiderVisible(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Lock body scroll when the mobile drawer is open
  useEffect(() => {
    if (!hasNavigationSider) return undefined;
    document.body.style.overflow = isMobile && siderVisible ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, siderVisible, hasNavigationSider]);

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    if (isMobile) setSiderVisible(false);
  }, [location.pathname, isMobile]);

  // Escape closes the mobile navigation drawer
  useEffect(() => {
    if (!hasNavigationSider || !isMobile || !siderVisible) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSiderVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasNavigationSider, isMobile, siderVisible]);

  const toggleSider = () => setSiderCollapsed((c) => !c);
  const toggleSiderVisible = () => setSiderVisible((v) => !v);
  const closeOnOverlay = useCallback(() => setSiderVisible(false), []);

  // On mobile the drawer is always full-width; collapse only applies on desktop.
  const effectiveCollapsed = isMobile ? false : siderCollapsed;

  return (
    <Layout className={classNames('layout', { 'has-navigation-sider': hasNavigationSider })}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header
        hasSider={hasNavigationSider}
        siderWidth={WIDTH_SIDER}
        siderVisible={siderVisible}
        toggleSiderVisible={toggleSiderVisible}
      />
      {showNotification && showSmsSettingBanner && <TwoFactorBanner />}
      <Layout>
        {hasNavigationSider && (
          <>
            {/* Backdrop — tapping it closes the mobile drawer */}
            <div
              className={classNames('sider-overlay', { 'sider-overlay--active': isMobile && siderVisible })}
              onClick={closeOnOverlay}
              aria-hidden="true"
            />
            <Layout.Sider
              id="navigation-sider"
              width={WIDTH_SIDER}
              collapsible
              collapsed={effectiveCollapsed}
              trigger={null}
              aria-label="Primary navigation"
              className={classNames('sider', {
                show: siderVisible,
                collapsed: !isMobile && siderCollapsed,
              })}
            >
              <div className="sider-shell">
                {/* Toggle only for admin sider; main sider renders its own in the brand header */}
                {!hasSider && (
                  <Button
                    type="link"
                    icon={siderCollapsed ? 'menu-unfold' : 'menu-fold'}
                    className="sider-toggle-btn"
                    onClick={toggleSider}
                    aria-label={siderCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  />
                )}
                <div className="sider-shell-body">
                  {sider && React.isValidElement(sider)
                    ? React.cloneElement(sider, { navigationSiderCollapsed: effectiveCollapsed })
                    : sider}
                  {hasSider && <Sider collapsed={effectiveCollapsed} toggle={toggleSider} />}
                </div>
              </div>
            </Layout.Sider>
          </>
        )}
        <Content
          id="main-content"
          className="content"
          style={{ backgroundImage: bg ? `url(${bg})` : null }}
          tabIndex={-1}
        >
          <SectionGuidanceVisibilityProvider>
            <PageTransition key={location.pathname}>{children}</PageTransition>
          </SectionGuidanceVisibilityProvider>
        </Content>
      </Layout>
      <Footer />
      <BackTop />
      <ContextSwitchOverlay active={switchingContext} message={contextSwitchMessage} />
    </Layout>
  );
};
